'use client';
/* eslint-disable tailwindcss/no-arbitrary-value -- the content width and the secondary text colour are tokens reached as CSS variables, the icon slot (22px) and the drawer's shadow are the design's own values, and no utility carries them */

import classNames from 'classnames';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { BaseTranslations } from '../../../baseTranslations';
import { MaterialIcon } from '../../../components/MaterialIcon';
import { useCmsClientContext } from '../../../utils/cmsPageContext';
import { sessionAttributionTracker } from '../../../utils/sessionAttributionTracking/sessionAttributionTracker';
import { getClientTranslations } from '../../../utils/translations/getClientTranslations';
import type { CompareCandidate } from '../compareCandidates';
import { compareIdOf } from '../compareUrl';
import {
  COMPARE_VARIANT_LAYOUT,
  compareCta,
  compareVariantCopy,
  compareVariantOf,
  type CompareVariant,
} from '../compareVariant';
import { TRANSLATIONS } from '../i18n';
import {
  bestPremiumIndex,
  resolveSections,
  skeletonSections,
} from '../resolveRows';
import {
  type CompareTracking,
  trackBackToTop,
  trackTableAddPlan,
  trackTableCta,
  trackTableRemove,
} from '../tracking';
import type {
  CompareConfig,
  CompareMetaItem,
  ComparePlan,
  ResolvedCompareSection,
} from '../types';
import { ChoosePlansModal } from './ChoosePlansModal';
import { PlanColumns } from './PlanColumns';
import { ScrollSyncProvider } from './ScrollSync';
import { SectionTable } from './SectionTable';

export type ComparePageProps = {
  config: CompareConfig;
  plans: ComparePlan[];
  /** Plans the shopper may still add; the caller leaves out the ones already compared. */
  candidates: CompareCandidate[];
  /** The line under the title: a trip's destinations, dates and travellers on TI. */
  meta?: CompareMetaItem[];
  /** The breadcrumb's first crumb: the site's home. */
  homeHref?: string;
  /** Breadcrumb / back target: the results page with its search params. */
  backHref: string;
  onRemove: (id: string) => void;
  onAdd: (candidate: CompareCandidate) => void;
  /** While the plans load: the header, `columns` loading cards at a card's height, and every configured row (`skeletonSections`). */
  loading?: { columns: number };
  /** The page's analytics, for the vertical the tracking plan covers; absent, nothing is sent. */
  tracking?: CompareTracking;
};

/**
 * The comparison page body. Values come from the compared products (and their premiums) —
 * nothing is fetched here; the app decides where the plans come from. A config for a vertical that
 * does not compare renders nothing.
 */
export const ComparePage = (props: ComparePageProps) => {
  const variant = compareVariantOf(props.config.vertical);
  return variant ? <ComparePageBody {...props} variant={variant} /> : null;
};

const ComparePageBody = ({
  config,
  plans: allPlans,
  candidates,
  meta = [],
  homeHref = '/',
  backHref,
  onRemove,
  onAdd,
  loading,
  tracking,
  variant,
}: ComparePageProps & { variant: CompareVariant }) => {
  const context = useCmsClientContext();
  const staticText = getClientTranslations(TRANSLATIONS, context);
  const baseText = getClientTranslations(BaseTranslations, context);
  const [chooserOpen, setChooserOpen] = useState(false);

  // A plan with no uid cannot be named in the selection, so it has no header column and cannot be
  // removed. Dropping it here rather than at render keeps the values, the padding and the header on
  // the same count — a plan filtered out further down would shift every value one column left of
  // its header, which is the one failure this page must never produce.
  const plans = useMemo(
    () => allPlans.filter((plan) => !!compareIdOf(plan.product)),
    [allPlans],
  );

  // Adding needs something to add: below the cap, and with a plan left to offer. Without one there
  // is no Add Plan at all rather than a control that opens an empty chooser. The empty column goes
  // with it.
  const canAdd =
    !loading && plans.length < config.selectionCap && candidates.length > 0;
  const layout = COMPARE_VARIANT_LAYOUT[variant];
  const copy = compareVariantCopy(variant, staticText);
  const pageTitle = copy.pageTitle;
  // The empty columns after the plans, which every row pads to so the columns cannot drift.
  const addSlots = !canAdd
    ? 0
    : layout.addSlots === 'all'
      ? config.selectionCap - plans.length
      : 1;

  // A number, not the prop object, so the memo does not recompute on every render.
  const loadingColumns = loading?.columns;
  const sections = useMemo(
    () =>
      loadingColumns !== undefined
        ? skeletonSections(config, loadingColumns)
        : resolveSections(config, plans, {
            included: staticText.compare_included,
            notIncluded: staticText.compare_not_included,
            offerLabels: {
              EXCLUSIVE_OFFER: staticText.compare_exclusive_offer,
              WELCOME_OFFER: staticText.compare_welcome_offer,
            },
          }),
    [
      config,
      plans,
      loadingColumns,
      staticText.compare_included,
      staticText.compare_not_included,
      staticText.compare_exclusive_offer,
      staticText.compare_welcome_offer,
    ],
  );
  // The price is a cell like any other: where the vertical marks nothing, it is not marked either.
  const premiumBest = useMemo(
    () => (config.highlightEnabled === false ? null : bestPremiumIndex(plans)),
    [plans, config.highlightEnabled],
  );

  // The drawer pins once the plan header cards have scrolled out above the viewport.
  const headerRef = useRef<HTMLDivElement>(null);
  const [pinned, setPinned] = useState(false);
  useEffect(() => {
    const node = headerRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) =>
        setPinned(!entry.isIntersecting && entry.boundingClientRect.top < 0),
      { threshold: 0 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // A vertical names its own sections, so the editor's label is the heading and a section left
  // without one has none. The key is a handle for analytics and tests; the page reads no meaning
  // into it.
  const sectionHeading = (label?: string) => label?.trim() || undefined;

  const collapsible = config.collapsibleSections === true;
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const isExpanded = (section: ResolvedCompareSection) =>
    collapsed[section.key] ?? section.defaultExpanded;
  const allExpanded = sections.every(isExpanded);

  // The trip details box beside the plan cards (from 1280): the header's line under a heading,
  // wrapping as the box's width allows. From 1280 it replaces that line in the header. An item
  // after a dot is glued to the one before it, so a line never starts with the dot.
  const tripBoxFromMeta = meta.length > 0;
  const tripGroups = meta.reduce<CompareMetaItem[][]>((groups, item) => {
    const last = groups[groups.length - 1];
    if (item.separatorBefore && last) last.push(item);
    else groups.push([item]);
    return groups;
  }, []);
  // Beside the cards it is a framed card; in the sticky drawer the same content sits unframed and
  // centred in the drawer's height.
  const tripBox = (framed: boolean) =>
    tripBoxFromMeta ? (
      <div
        data-component="compare-trip-box"
        className={classNames(
          'flex h-full flex-col gap-spacing/gap/y-sm px-spacing/gap/x py-spacing/padding/y-sm',
          framed
            ? 'rounded-radius/card border border-borders/default bg-fill/base'
            : 'justify-center bg-fill/card',
        )}
      >
        <span className="mhg-alpha-typography-subheading-2 text-text/primary">
          {staticText.compare_trip_details}
        </span>
        <div className="flex flex-wrap items-center gap-x-spacing/gap/soft-12px gap-y-spacing/gap/soft-6px">
          {tripGroups.map((group) => (
            <span
              key={group.map((item) => `${item.icon}-${item.text}`).join('|')}
              className="flex min-w-0 items-center gap-spacing/gap/soft-12px"
            >
              {group.map((item) => (
                <MetaItem
                  key={`${item.icon}-${item.text}`}
                  item={item}
                  gap="gap-spacing/gap/soft-4px"
                  iconSlot
                />
              ))}
            </span>
          ))}
        </div>
      </div>
    ) : undefined;

  const planColumnProps = {
    plans,
    bestPremiumIndex: premiumBest,
    addSlots,
    addLabel: copy.slotLabel,
    layout,
    ctaFor: (plan: ComparePlan) =>
      compareCta(variant, plan.product, staticText, baseText),
    onRemove: (plan: ComparePlan, index: number) => {
      if (tracking) trackTableRemove(tracking, { plan: { ...plan, index } });
      // Every plan here has an id: one without is dropped before the columns.
      onRemove(compareIdOf(plan.product)!);
    },
    onAddPlan: (column: number) => {
      if (tracking)
        trackTableAddPlan(tracking, {
          clickText: copy.slotLabel,
          index: column,
        });
      setChooserOpen(true);
    },
    onCtaClick: (plan: ComparePlan, index: number, label: string) => {
      // An apply from the table attributes the lead to this page on every variant, as every other
      // apply CTA does; the GA event follows the tracking plan.
      sessionAttributionTracker.recordApplyClick();
      if (tracking)
        trackTableCta(tracking, {
          clickText: label,
          plan: { ...plan, index },
        });
    },
    staticText,
    labelSlot: tripBox(true),
    placeholders: loading?.columns ?? 0,
  };

  return (
    <ScrollSyncProvider
      resetKey={plans.map((plan) => compareIdOf(plan.product)).join(',')}
    >
      <div
        data-component="compare-page"
        data-count={plans.length}
        data-loading={loading ? true : undefined}
        className="w-full lg:pb-spacing/padding/section-y"
      >
        {/* The content column; the sticky drawer sits outside it so from 1280 it can run the full
            width of the viewport, as the design draws it. */}
        <div className={pageColumnClass}>
          <header
            data-component="compare-header"
            className="flex flex-col gap-spacing/gap/y-sm pt-spacing/padding/section-y lg:pb-spacing/gap/y-sm"
          >
            <nav
              aria-label="Breadcrumb"
              className="mhg-alpha-typography-breadcrumb flex min-w-0 items-center gap-spacing/gap/x-xs text-text/primary"
            >
              <a
                // Internal navigation: not `FeLink`, which treats an href containing "ad" as an advert.
                href={homeHref}
                aria-label={staticText.compare_breadcrumb_home}
                className="flex shrink-0"
              >
                <MaterialIcon icon="home" px={16} />
              </a>
              <span aria-hidden="true" className="opacity-40">
                /
              </span>
              <a href={backHref} className="shrink-0 opacity-80">
                {copy.backLabel}
              </a>
              <span aria-hidden="true" className="opacity-40">
                /
              </span>
              <span
                aria-current="page"
                className="min-w-0 flex-1 truncate underline opacity-80"
              >
                {pageTitle}
              </span>
            </nav>
            <div className="flex items-start justify-between gap-spacing/gap/x">
              <div className="flex flex-col gap-spacing/gap/y-xs">
                <h1 className="mhg-alpha-typography-display-3 text-text/primary">
                  {pageTitle}
                </h1>
                {layout.countLine && (
                  <p
                    data-component="compare-count"
                    className="mhg-alpha-typography-body-2 text-text/secondary-lighter"
                  >
                    {copy.countLine(plans.length, config.selectionCap)}
                  </p>
                )}
              </div>
              {layout.headerAdd && canAdd && (
                // Credit card adds from the header too (BRD-02); below 1280 the empty column does it.
                <button
                  type="button"
                  data-component="compare-header-add"
                  onClick={() => setChooserOpen(true)}
                  className="mhg-alpha-typography-button-2 hidden h-9 shrink-0 items-center gap-spacing/gap/x-xs rounded-radius/button border border-buttons/primary bg-buttons/base px-spacing/padding/x text-buttons/primary lg:inline-flex"
                >
                  <MaterialIcon icon="add" px={18} />
                  {copy.addLabel}
                </button>
              )}
            </div>
            {meta.length > 0 && (
              <div
                data-component="compare-meta"
                // From 1280 the trip details box beside the plan cards carries these items instead.
                className={classNames(
                  'flex flex-wrap items-center gap-x-spacing/gap/x-sm gap-y-spacing/gap/y-xs',
                  tripBoxFromMeta && 'lg:hidden',
                )}
              >
                {meta.map((item) => (
                  <MetaItem
                    key={`${item.icon}-${item.text}`}
                    item={item}
                    gap="gap-spacing/gap/x-xs"
                  />
                ))}
              </div>
            )}
          </header>

          <div
            ref={headerRef}
            // While the drawer stands in for these cards they are scrolled out of view, and a
            // second set of the same controls must not stay in the tab order or be read twice.
            inert={pinned && !loading}
            aria-hidden={pinned && !loading ? true : undefined}
            className="pt-spacing/padding/section-y"
          >
            <PlanColumns {...planColumnProps} />
          </div>
        </div>

        {/*
          A zero-height sticky anchor, always in the flow: the drawer hangs from it, so pinning and
          unpinning never change the page's height. Inserting the drawer itself would push the rows
          under the reader's thumb down by its height (Safari has no scroll anchoring to hide it).
        */}
        <div className="sticky top-0 z-40 h-0">
          {pinned && !loading && (
            <div
              data-component="compare-sticky-drawer"
              // Below 1280 the drawer keeps the page margin and bleeds 16px past it; from
              // 1280 its band spans the viewport and its content keeps the page column.
              className="px-spacing/margin/x lg:px-0"
            >
              <div
                // The design's default shadow: the project's `shadow` is a different, heavier one.
                className="-mx-4 bg-fill/card px-4 shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1)] lg:mx-0 lg:px-spacing/margin/x"
              >
                <div className="mx-auto max-w-[--breakpoints-content-narrow-3-lg]">
                  <PlanColumns
                    {...planColumnProps}
                    labelSlot={tripBox(false)}
                    compact
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className={pageColumnClass}>
          {collapsible && sections.length > 0 && (
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                data-component="compare-expand-all"
                onClick={() =>
                  setCollapsed(
                    Object.fromEntries(
                      sections.map((section) => [section.key, !allExpanded]),
                    ),
                  )
                }
                className="mhg-alpha-typography-bullet-2 text-buttons/primary underline"
              >
                {allExpanded
                  ? staticText.compare_collapse_all
                  : staticText.compare_expand_all}
              </button>
            </div>
          )}

          {sections.map((section, index) => (
            <Fragment key={section.key}>
              <SectionTable
                section={section}
                padColumns={addSlots}
                heading={sectionHeading(section.label)}
                note={section.note}
                legend={
                  section.showLegend
                    ? {
                        highlight: staticText.compare_legend_highlight,
                        rest: staticText.compare_legend_rest,
                      }
                    : undefined
                }
                emptyValue={staticText.compare_empty_value}
                placeholder={!!loading}
                scrollLabels={{
                  left: staticText.compare_scroll_left,
                  right: staticText.compare_scroll_right,
                }}
                collapse={
                  collapsible
                    ? {
                        expanded: isExpanded(section),
                        onToggle: () =>
                          setCollapsed((current) => ({
                            ...current,
                            [section.key]: !isExpanded(section),
                          })),
                        label: sectionHeading(section.label) ?? section.key,
                      }
                    : undefined
                }
              />
              {/* Closes an offers section wherever it sits, and the page. */}
              {closesWithBackToTop(sections, index) && (
                <BackToTop
                  label={staticText.compare_back_to_top}
                  onClick={() => {
                    if (tracking)
                      trackBackToTop(tracking, {
                        clickText: staticText.compare_back_to_top,
                        index: sections
                          .slice(0, index)
                          .filter((_, before) =>
                            closesWithBackToTop(sections, before),
                          ).length,
                      });
                  }}
                />
              )}
            </Fragment>
          ))}

          {!loading && (
            <ChoosePlansModal
              isOpen={chooserOpen}
              onClose={() => setChooserOpen(false)}
              candidates={candidates}
              onPick={(candidate) => {
                onAdd(candidate);
                setChooserOpen(false);
              }}
              title={copy.chooseTitle}
              searchPlaceholder={copy.searchPlaceholder}
              noMatch={{ title: copy.noMatchTitle, hint: copy.noMatchHint }}
              layout={layout}
            />
          )}
        </div>
      </div>
    </ScrollSyncProvider>
  );
};

/**
 * The page's content column. 1130 is the content width the ticket's grid adds up to
 * (350 + 3 × 260); from 1280 the page margin sits outside it rather than eating into the columns.
 */
const pageColumnClass =
  'mx-auto w-full max-w-[--breakpoints-content-narrow-3-lg] px-spacing/margin/x lg:box-content';

/** "Back to top" closes an offers section wherever it sits, and the page. */
const closesWithBackToTop = (
  sections: ResolvedCompareSection[],
  index: number,
) =>
  index === sections.length - 1 ||
  sections[index].rows.some((row) => row.source !== 'ATTRIBUTE');

/**
 * "↑ Back to top", closing an offers section and the page, as the design places it: on the same
 * full-width white band as the table above it (below 1280, where the table has one).
 */
const BackToTop = ({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) => (
  <div className="-mx-spacing/margin/x bg-fill/card px-spacing/margin/x py-spacing/gap/section-y lg:mx-0 lg:bg-transparent lg:px-0">
    <button
      type="button"
      data-component="compare-back-to-top"
      onClick={() => {
        onClick();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }}
      className="mhg-alpha-typography-body-1 inline-flex items-center gap-spacing/gap/soft-4px text-text/secondary-lighter"
    >
      <MaterialIcon icon="arrow_upward" px={18} />
      <span className="underline">{label}</span>
    </button>
  </div>
);

/**
 * One item of the trip line: an optional dot before it, an icon and the text. Shared by the header
 * line (below 1280) and the trip details box (from 1280), which differ only in gap and icon slot.
 */
const MetaItem = ({
  item,
  gap,
  iconSlot = false,
}: {
  item: CompareMetaItem;
  gap: string;
  iconSlot?: boolean;
}) => (
  <>
    {item.separatorBefore && (
      <span
        aria-hidden="true"
        className="size-1 shrink-0 rounded-full bg-[--text-secondary]"
      />
    )}
    <span
      className={classNames(
        'mhg-alpha-typography-bullet-2 flex min-w-0 items-center py-spacing/padding/soft-4px text-[--text-secondary]',
        gap,
      )}
    >
      {iconSlot ? (
        // The design's icon slot: 22px at 1280, 24px at 1440, around an 18px glyph.
        <span className="flex size-[22px] shrink-0 items-center justify-center xl:size-6">
          <MaterialIcon icon={item.icon} px={18} />
        </span>
      ) : (
        <MaterialIcon icon={item.icon} px={18} />
      )}
      <span className="truncate">{item.text}</span>
    </span>
  </>
);
